// tslint:disable: max-line-length
import { Action, State, StateContext, Store } from '@ngxs/store';
import { empty, Observable, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { AddMembers, AddMembersFailed, AddOwners, AddOwnersFailed, CreateGroup, CreateGroupFailed, EditGroup, GetGroups, GetGroupsFailed, GetUsers, GetUsersFailed, RemoveMember, RemoveMemberFailed, RemoveOwner, RemoveOwnerFailed, ResetGroupManagementState, SaveGroup, SaveGroupFailed, SelectGroup, } from './group-management.actions';

import { Injectable } from '@angular/core';
import { User } from 'app/classes/user';
import { cloneDeep } from 'lodash';
import { Group } from '../models/group.model';
import { DataService } from '../services/data.service';

// tslint:enable: max-line-length


export interface GroupManagementStateModel {
  groups: Group[];
  selected: Group;
  users: User[];
  loadingUsers: boolean;
  currentUser: User;
  initializing: boolean;
  saving: boolean;
  editing: boolean;
  removingUsers: {};
  errorMessage: string;
}

const defaults = {
  groups: [],
  selected: undefined,
  users: [],
  loadingUsers: false,
  currentUser: undefined,
  initializing: false,
  saving: false,
  editing: false,
  removingUsers: {},
  errorMessage: '',
} as GroupManagementStateModel;


@State<GroupManagementStateModel>({
  name: 'groupManagement',
  defaults: { ...defaults },
})
@Injectable()
export class GroupManagementState {
  constructor(private dataService: DataService, private store: Store) {
  }

  @Action(ResetGroupManagementState)
  resetGroupManagementState(ctx: StateContext<GroupManagementStateModel>): void {
    ctx.setState({
      ...defaults
    });
  }

  @Action(GetGroups)
  getGroups(ctx: StateContext<GroupManagementStateModel>): Observable<Group[]> {
    ctx.setState({
      ...defaults,
      initializing: true,
    });

    return this.dataService.getGroups().pipe(
      tap((groups: Group[]) => {
        ctx.setState({
          ...defaults,
          groups: groups,
          initializing: false,
        });
      }),
      catchError(err => {
        this.store.dispatch(new GetGroupsFailed(err));
        return empty();
      }),
    );
  }

  @Action(SelectGroup)
  selectGroup(ctx: StateContext<GroupManagementStateModel>, action: SelectGroup): void {
    const state = ctx.getState();

    ctx.setState({
      ...state,
      selected: action.group,
      editing: false,
    });
  }

  @Action(EditGroup)
  editGroup(ctx: StateContext<GroupManagementStateModel>, action: EditGroup): void {
    const state = ctx.getState();

    ctx.setState({
      ...state,
      editing: action.editing,
    });
  }

  @Action(GetUsers)
  getUsers(ctx: StateContext<GroupManagementStateModel>): Observable<User[]> {
    let state = ctx.getState();
    ctx.setState({
      ...state,
      loadingUsers: true,
    });

    return this.dataService.getAllUsers().pipe(
      tap(users => {
        state = ctx.getState();
        ctx.setState({
          ...state,
          loadingUsers: false,
          users: users,
        });
      }),
      catchError(err => {
        this.store.dispatch(new GetUsersFailed(err));
        return empty();
      }),
    );
  }

  @Action(CreateGroup)
  createGroup(ctx: StateContext<GroupManagementStateModel>, action: CreateGroup): Observable<Group> {
    let state = ctx.getState();
    ctx.setState({
      ...state,
      saving: true,
    });

    return this.dataService.createGroup(action.group).pipe(
      tap((response) => {
        state = ctx.getState();
        ctx.setState({
          ...state,
          saving: false,
          errorMessage: '',
          groups: [...state.groups, response],
        });

        this.store.dispatch(new SelectGroup(response));
      }),
      catchError((err: any) => {
        this.store.dispatch(new CreateGroupFailed(err.message));
        return of(null);
      }),
    );
  }

  @Action(CreateGroupFailed)
  createGroupFailed(ctx: StateContext<GroupManagementStateModel>, action: any): void {
    const state = ctx.getState();
    ctx.setState({
      ...state,
      errorMessage: 'Create group failed. \n' + action.reason,
      saving: false,
    });
  }

  @Action(SaveGroup)
  saveGroup(ctx: StateContext<GroupManagementStateModel>, action: SaveGroup): Observable<void> {
    let state = ctx.getState();
    ctx.setState({
      ...state,
      saving: true,
    });

    return this.dataService.updateGroup(action.group).pipe(
      tap(() => {
        state = ctx.getState();
        const groups = cloneDeep<Group[]>(state.groups);
        const index = groups.findIndex(g => g.uuid === action.group.uuid);
        if (index > -1) {
          groups[index].name = action.group.name;
          groups[index].privateGroup = action.group.privateGroup;
        }

        ctx.setState({
          ...state,
          groups: groups,
          saving: false,
          errorMessage: '',
        });
      }),
      catchError(err => {
        this.store.dispatch(new SaveGroupFailed(err.message));
        return of(null);
      }),
    );
  }

  @Action(SaveGroupFailed)
  saveGroupFailed(ctx: StateContext<GroupManagementStateModel>, action: SaveGroupFailed): void {
    const state = ctx.getState();
    ctx.setState({
      ...state,
      errorMessage: `Save group failed: ${action.reason}`,
      saving: false,
    });
  }

  @Action(AddMembers)
  addMembers(ctx: StateContext<GroupManagementStateModel>, action: AddMembers): Observable<Group> {
    let state = ctx.getState();
    ctx.setState({
      ...state,
      saving: true,
    });

    const group = cloneDeep<Group>(action.group);

    return this.dataService.inviteMarines(group.uuid, action.emails).pipe(
      tap(response => {
        group.members = response.members;

        state = ctx.getState();
        ctx.setState({
          ...state,
          selected: group,
          groups: this.updateGroups(state.groups, group),
          saving: false,
        });
      }),
      catchError(err => {
        this.store.dispatch(new AddMembersFailed(err));
        return empty();
      }),
    );
  }

  @Action(AddOwners)
  addOwners(ctx: StateContext<GroupManagementStateModel>, action: AddOwners): Observable<void> {
    let state = ctx.getState();
    ctx.setState({
      ...state,
      saving: true,
    });

    const group = cloneDeep<Group>(action.group);

    return this.dataService
      .addOwners(
        group.uuid,
        action.users.map(u => u.uuid),
      )
      .pipe(
        tap(() => {
          group.addOwners(action.users);
          state = ctx.getState();

          ctx.setState({
            ...state,
            groups: this.updateGroups(state.groups, group),
            selected: group,
            saving: false,
          });
        }),
        catchError(err => {
          this.store.dispatch(new AddOwnersFailed(err));
          return empty();
        }),
      );
  }

  private updateGroups(groups: Group[], group: Group): Group[] {
    const clone = cloneDeep<Group[]>(groups);
    const index = clone.findIndex(g => g.uuid === group.uuid);
    if (index > -1) {
      clone[index] = group;
    }

    return clone;
  }

  @Action(RemoveMember)
  removeMembers(ctx: StateContext<GroupManagementStateModel>, action: RemoveMember): Observable<void> {
    return this.removeFromGroup(ctx, action);
  }

  @Action(RemoveOwner)
  removeOwners(ctx: StateContext<GroupManagementStateModel>, action: RemoveOwner): Observable<void> {
    return this.removeFromGroup(ctx, action);
  }

  private removeFromGroup(ctx: StateContext<GroupManagementStateModel>, action: RemoveMember | RemoveOwner): Observable<void> {
    let state = ctx.getState();
    let removingUsers = cloneDeep(state.removingUsers);
    removingUsers[action.user.uuid] = true;

    ctx.setState({
      ...state,
      saving: true,
      removingUsers: removingUsers,
    });

    let request: Observable<void>;
    if (action instanceof RemoveMember) {
      request = this.dataService.removeMarine(action.group.uuid, action.user.uuid);
    } else if (action instanceof RemoveOwner) {
      request = this.dataService.removeOwner(action.group.uuid, action.user.uuid);
    } else {
      request = empty();
    }

    return request.pipe(
      tap(() => {
        state = ctx.getState();
        removingUsers = cloneDeep(state.removingUsers);
        removingUsers[action.user.uuid] = false;

        const selected = cloneDeep<Group>(state.selected);
        if (action instanceof RemoveMember) {
          selected.members = selected.members.filter(u => u.uuid !== action.user.uuid);
        } else if (action instanceof RemoveOwner) {
          selected.owners = selected.owners.filter(u => u.uuid !== action.user.uuid);
        }

        ctx.setState({
          ...state,
          selected: selected,
          saving: false,
          removingUsers: removingUsers,
        });
      }),
      catchError(err => {
        if (action instanceof RemoveMember) {
          this.store.dispatch(new RemoveMemberFailed(err));
        } else if (action instanceof RemoveOwner) {
          this.store.dispatch(new RemoveOwnerFailed(err));
        }

        return empty();
      }),
    );
  }
}
